import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import TopHeader from "./TopHeader";
import { Outlet, useLocation } from "react-router-dom";
import AdminSideBar from "./AdminSideBar";
import { cn } from "../../../lib/utils";

// Animation variants
const sidebarVariants = {
  open: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      duration: 0.3,
    },
  },
  closed: {
    x: -280,
    opacity: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      duration: 0.3,
    },
  },
};

const contentVariants = {
  initial: {
    opacity: 0,
    y: 10,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

const overlayVariants = {
  open: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
  closed: {
    opacity: 0,
    transition: { duration: 0.3 },
  },
};

export const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Auto-close mobile sidebar when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleItemClick = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <div className="flex min-h-screen bg-background">
        {/* Desktop sidebar with animation */}
        <motion.div
          className="hidden lg:block"
          animate={{
            width: collapsed ? 68 : 250,
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            duration: 0.3,
          }}
        >
          <AdminSideBar
            collapsed={collapsed}
            onToggle={() => setCollapsed(!collapsed)}
            onItemClick={handleItemClick}
            mobile={false}
          />
        </motion.div>

        {/* Mobile sidebar with animation */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Overlay with fade animation */}
              <motion.div
                className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                variants={overlayVariants}
                initial="closed"
                animate="open"
                exit="closed"
                onClick={() => setMobileOpen(false)}
              />

              {/* Sidebar slide animation */}
              <motion.div
                className="fixed left-0 top-0 z-50 h-full w-[250px] lg:hidden"
                variants={sidebarVariants}
                initial="closed"
                animate="open"
                exit="closed"
              >
                <AdminSideBar
                  collapsed={false}
                  onToggle={() => setMobileOpen(false)}
                  onItemClick={handleItemClick}
                  mobile={true}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main content */}
        <div className={cn("flex flex-1 flex-col min-w-0")}>
          {/* Top Header */}
          <TopHeader onMenuToggle={() => setMobileOpen(true)} />

          {/* Page content with fade animation */}
          <motion.main
            key={location.pathname}
            className="flex-1 overflow-auto p-4 lg:p-6"
            variants={contentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Outlet />
          </motion.main>
        </div>
      </div>
    </>
  );
};
